import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import {
  Waves, Lightning, Check, CreditCard, Spinner, Copy, CheckCircle,
  ArrowLeft, ArrowClockwise, WarningCircle, CurrencyBtc, CurrencyEth,
  QrCode, Timer, CaretRight, ShieldCheck, Sun, Moon, CurrencyInr,
  Bank, ClockCounterClockwise, CheckSquare
} from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';
import { Logo, LogoText } from '../components/Logo';

const COINS = [
  { id: 'btc', name: 'Bitcoin', symbol: 'BTC', gasFee: 0 },
  { id: 'eth', name: 'Ethereum', symbol: 'ETH', gasFee: 0 },
  { id: 'bnbbsc', name: 'BNB (BSC)', symbol: 'BNB', gasFee: 0 },
  { id: 'sol', name: 'Solana', symbol: 'SOL', gasFee: 0 },
  { id: 'usdtbsc', name: 'USDT (BEP20)', symbol: 'USDT', network: 'BEP20', gasFee: 0 },
  { id: 'usdttrc20', name: 'USDT (TRC20)', symbol: 'USDT', network: 'TRC20', gasFee: 5 },
  { id: 'ltc', name: 'Litecoin', symbol: 'LTC', gasFee: 0 },
  { id: 'doge', name: 'Dogecoin', symbol: 'DOGE', gasFee: 0 },
  { id: 'trx', name: 'Tron', symbol: 'TRX', gasFee: 0 },
  { id: 'xrp', name: 'Ripple', symbol: 'XRP', gasFee: 0 },
  { id: 'matic', name: 'Polygon', symbol: 'MATIC', gasFee: 0 }
];

const PLANS = {
  starter: { name: 'Starter', price: 20, credits: 1000 },
  growth: { name: 'Growth', price: 90, credits: 5000 },
  pro: { name: 'Pro', price: 225, credits: 15000 },
  business: { name: 'Business', price: 650, credits: 50000 },
  enterprise: { name: 'Enterprise', price: 1050, credits: 100000 }
};

const INR_RATE = 100;

const STATUS_STEPS = [
  { id: 'waiting', label: 'Awaiting Deposit', description: 'Send crypto to the address below' },
  { id: 'confirming', label: 'Confirming', description: 'Transaction detected, confirming...' },
  { id: 'confirmed', label: 'Processing', description: 'Payment confirmed, adding credits...' },
  { id: 'finished', label: 'Complete', description: 'Credits added to your account!' }
];

export default function Payment() {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const planId = searchParams.get('plan') || 'starter';
  const plan = PLANS[planId] || PLANS.starter;
  const planInr = plan.price * INR_RATE;

  // Payment method tab
  const [paymentMethod, setPaymentMethod] = useState('crypto');
  const [inrEnabled, setInrEnabled] = useState(false);
  const [cryptoEnabled, setCryptoEnabled] = useState(true);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  const effectivePrice = couponData ? couponData.final_price : plan.price;
  const effectiveInr = effectivePrice * INR_RATE;

  // Crypto state
  const [step, setStep] = useState('select_coin');
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('waiting');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [autoCheckCountdown, setAutoCheckCountdown] = useState(15);

  // INR state
  const [upiInfo, setUpiInfo] = useState(null);
  const [utrNumber, setUtrNumber] = useState('');
  const [inrStep, setInrStep] = useState('pay');
  const [inrLoading, setInrLoading] = useState(false);
  const [inrHistory, setInrHistory] = useState([]);

  const selectedCoinData = COINS.find(c => c.id === selectedCoin);
  const gasFee = selectedCoinData?.gasFee || 0;
  const totalUSD = plan.price + gasFee;

  // Fetch UPI info when INR tab is selected
  useEffect(() => {
    if (paymentMethod === 'inr') {
      fetchUpiInfo();
      fetchInrHistory();
    }
  }, [paymentMethod]);

  // Fetch payment settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/pricing/plans`, { withCredentials: true });
        setInrEnabled(res.data.inr_enabled || false);
        setCryptoEnabled(res.data.crypto_enabled !== false);
        if (!res.data.crypto_enabled && res.data.inr_enabled) setPaymentMethod('inr');
      } catch (err) { console.error(err); }
    };
    fetchSettings();
  }, []);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponData(null);
    try {
      const res = await axios.post(`${API_URL}/api/pricing/apply-coupon`, { code: couponCode.trim(), plan_id: planId }, { withCredentials: true });
      setCouponData(res.data);
      toast.success(`Coupon applied! You save $${res.data.discount_amount}`);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponData(null);
    setCouponCode('');
    setCouponError('');
  };

  const fetchUpiInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payments/upi-info`, { withCredentials: true });
      setUpiInfo(res.data);
    } catch (err) {
      setUpiInfo(null);
    }
  };

  const fetchInrHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payments/inr-history`, { withCredentials: true });
      setInrHistory(res.data || []);
    } catch (err) { console.error(err); }
  };

  // Auto-check crypto payment status
  useEffect(() => {
    if (step !== 'payment' || !paymentData || paymentStatus === 'finished' || paymentStatus === 'failed' || paymentStatus === 'expired') return;
    const timer = setInterval(() => {
      setAutoCheckCountdown(prev => {
        if (prev <= 1) { checkPaymentStatus(); return 15; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step, paymentData, paymentStatus]);

  const checkPaymentStatus = async () => {
    if (!paymentData?.payment_id) return;
    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${paymentData.payment_id}`, { withCredentials: true });
      const newStatus = response.data.status;
      setPaymentStatus(newStatus);
      if (newStatus === 'finished') {
        refreshUser();
        toast.success('Payment complete! Credits added to your account.');
      }
    } catch (err) { console.error('Status check error:', err); }
  };

  const handleCreatePayment = async () => {
    if (!selectedCoin) { toast.error('Select a payment method'); return; }
    setLoading(true); setError('');
    try {
      const response = await axios.post(`${API_URL}/api/payments/create`, {
        plan_id: planId, pay_currency: selectedCoin
      }, { withCredentials: true });
      setPaymentData(response.data); setPaymentStatus('waiting'); setStep('payment'); setAutoCheckCountdown(15);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to create payment'); }
    setLoading(false);
  };

  const handleSubmitUtr = async () => {
    if (!utrNumber.trim() || utrNumber.trim().length < 6) {
      toast.error('Please enter a valid UTR number (min 6 characters)');
      return;
    }
    setInrLoading(true);
    try {
      await axios.post(`${API_URL}/api/payments/inr-request`, {
        plan_id: planId, utr_number: utrNumber.trim()
      }, { withCredentials: true });
      toast.success('Payment submitted! Awaiting admin approval.');
      setInrStep('submitted');
      setUtrNumber('');
      fetchInrHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit payment');
    }
    setInrLoading(false);
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
    toast.success('Copied!');
  };

  const getStatusIndex = () => {
    const idx = STATUS_STEPS.findIndex(s => s.id === paymentStatus);
    return idx >= 0 ? idx : 0;
  };

  const getInrStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">Pending</span>;
      case 'approved': return <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-500/20 text-emerald-400">Approved</span>;
      case 'rejected': return <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">Rejected</span>;
      default: return <span className="px-2 py-0.5 text-xs rounded-full" style={{ background: 'var(--accent-bg)', color: 'var(--text-muted)' }}>{status}</span>;
    }
  };

  if (!user) { navigate('/login'); return null; }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="payment-logo">
            <Logo size="md" />
            <LogoText size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 border px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)' }}>
              <CreditCard weight="duotone" className="w-4 h-4" style={{ color: 'var(--accent)' }} />
              <span className="font-bold">{user.credits || 0}</span>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>credits</span>
            </div>
            <button onClick={toggleTheme} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="payment-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/pricing" className="inline-flex items-center gap-2 text-sm transition-colors mb-6" style={{ color: 'var(--text-secondary)' }} data-testid="back-to-pricing">
            <ArrowLeft className="w-4 h-4" /> Back to pricing
          </Link>

          {/* Order Summary Card */}
          <div className="border rounded-xl p-6 mb-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <ShieldCheck weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />
              Order Summary
            </h2>
            <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border-main)' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Plan</span>
              <span className="font-bold">{plan.name}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border-main)' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Credits</span>
              <span className="font-bold" style={{ color: 'var(--accent)' }}>{plan.credits.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span style={{ color: 'var(--text-secondary)' }}>Price</span>
              <div className="text-right">
                <span className="font-bold">${plan.price} USD</span>
              </div>
            </div>

            {/* Coupon Code */}
            <div className="pt-3 border-t" style={{ borderColor: 'var(--border-main)' }}>
              {couponData ? (
                <div className="flex items-center justify-between py-2">
                  <div>
                    <span className="text-xs font-medium px-2 py-1 rounded" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>
                      {couponData.code} — {couponData.discount_type === 'percentage' ? `${couponData.discount_value}% off` : `$${couponData.discount_value} off`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-emerald-400">-${couponData.discount_amount}</span>
                    <button onClick={removeCoupon} className="text-xs underline" style={{ color: 'var(--text-muted)' }}>Remove</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                    placeholder="Coupon code"
                    className="flex-1 px-3 py-2 rounded-lg border text-sm"
                    style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
                    data-testid="coupon-input"
                  />
                  <button
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    className="px-4 py-2 rounded-lg text-sm font-bold transition-all"
                    style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}
                    data-testid="apply-coupon-btn"
                  >
                    {couponLoading ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              )}
              {couponError && <p className="text-red-400 text-xs mt-1">{couponError}</p>}
            </div>

            {/* Final Price */}
            {couponData && (
              <div className="flex items-center justify-between py-3 border-t" style={{ borderColor: 'var(--border-main)' }}>
                <span className="font-bold">Total</span>
                <div className="text-right">
                  <span className="text-lg font-black" style={{ color: 'var(--accent)' }}>${effectivePrice} USD</span>
                  <span className="text-xs line-through ml-2" style={{ color: 'var(--text-muted)' }}>${plan.price}</span>
                </div>
              </div>
            )}
          </div>

          {/* Payment Method Tabs */}
          <div className="flex gap-3 mb-6">
            {cryptoEnabled && (
              <button
                onClick={() => setPaymentMethod('crypto')}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-sm border-2 transition-all"
                style={{
                  borderColor: paymentMethod === 'crypto' ? 'var(--accent)' : 'var(--border-main)',
                  background: paymentMethod === 'crypto' ? 'var(--accent-bg)' : 'var(--bg-card)',
                  color: paymentMethod === 'crypto' ? 'var(--accent)' : 'var(--text-secondary)'
                }}
                data-testid="tab-crypto"
              >
                <CurrencyBtc weight="duotone" className="w-5 h-5" />
                Crypto
              </button>
            )}
            {inrEnabled && (
              <button
                onClick={() => setPaymentMethod('inr')}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-sm border-2 transition-all"
                style={{
                  borderColor: paymentMethod === 'inr' ? 'var(--accent)' : 'var(--border-main)',
                  background: paymentMethod === 'inr' ? 'var(--accent-bg)' : 'var(--bg-card)',
                  color: paymentMethod === 'inr' ? 'var(--accent)' : 'var(--text-secondary)'
                }}
                data-testid="tab-inr"
              >
                <CurrencyInr weight="duotone" className="w-5 h-5" />
                INR (UPI)
              </button>
            )}
          </div>

          {/* ========= CRYPTO PAYMENT FLOW ========= */}
          {paymentMethod === 'crypto' && (
            <>
              {step === 'select_coin' && (
                <div className="space-y-6">
                  <div className="border rounded-xl p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                    <h2 className="text-xl font-bold mb-4">Select Cryptocurrency</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {COINS.map(coin => (
                        <button
                          key={coin.id}
                          onClick={() => setSelectedCoin(coin.id)}
                          className="p-3 rounded-lg border transition-all text-left"
                          style={{
                            borderColor: selectedCoin === coin.id ? 'var(--border-accent)' : 'var(--border-main)',
                            background: selectedCoin === coin.id ? 'var(--accent-bg)' : 'transparent',
                            boxShadow: selectedCoin === coin.id ? '0 0 0 1px var(--accent)' : 'none'
                          }}
                          data-testid={`coin-${coin.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {coin.id === 'btc' && <CurrencyBtc weight="duotone" className="w-5 h-5 text-amber-500" />}
                            {coin.id === 'eth' && <CurrencyEth weight="duotone" className="w-5 h-5 text-purple-400" />}
                            {!['btc', 'eth'].includes(coin.id) && (
                              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-bg)' }}>
                                <span className="text-[8px] font-bold" style={{ color: 'var(--accent)' }}>{coin.symbol.charAt(0)}</span>
                              </div>
                            )}
                            <span className="font-bold text-sm">{coin.symbol}</span>
                          </div>
                          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{coin.network ? `${coin.name}` : coin.name}</p>
                          {coin.gasFee > 0 && <p className="text-[10px] text-amber-400 mt-1">+${coin.gasFee} gas fee</p>}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
                    <div className="flex gap-3">
                      <WarningCircle weight="fill" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-sm text-amber-400 mb-2">Important Note</h3>
                        <ul className="text-xs space-y-1.5" style={{ color: 'var(--text-secondary)' }}>
                          <li>Please send <strong className="text-amber-300">$0.05 - $0.10 extra</strong> than the displayed amount.</li>
                          <li>USDT TRC20 transactions include a <strong className="text-amber-300">$5 extra gas fee</strong>.</li>
                          <li>Credits are added automatically once payment is confirmed.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 text-sm rounded-lg">{error}</div>}

                  <button
                    onClick={handleCreatePayment}
                    disabled={loading || !selectedCoin}
                    className="w-full btn-primary py-4 flex items-center justify-center gap-2 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="proceed-to-pay"
                  >
                    {loading ? <Spinner className="w-6 h-6 animate-spin" /> : (
                      <><Lightning weight="fill" className="w-5 h-5" /> Pay ${totalUSD} with {selectedCoinData?.symbol || 'Crypto'}</>
                    )}
                  </button>
                </div>
              )}

              {step === 'payment' && paymentData && (
                <div className="space-y-6">
                  {/* Status Stepper */}
                  <div className="border rounded-xl p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                    <div className="space-y-4">
                      {STATUS_STEPS.map((s, i) => {
                        const currentIdx = getStatusIndex();
                        const isActive = i === currentIdx;
                        const isDone = i < currentIdx;
                        const isFailed = paymentStatus === 'failed' || paymentStatus === 'expired';
                        return (
                          <div key={s.id} className="flex items-start gap-4" data-testid={`status-step-${s.id}`}>
                            <div className="flex flex-col items-center">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                                isDone ? 'bg-emerald-500 border-emerald-500' :
                                isActive && !isFailed ? 'animate-pulse' :
                                isFailed && isActive ? 'border-red-500 bg-red-500/10' : ''
                              }`} style={isDone ? {} : isActive && !isFailed ? { borderColor: 'var(--border-accent)', background: 'var(--accent-bg)' } : isFailed && isActive ? {} : { borderColor: 'var(--border-main)', background: 'transparent' }}>
                                {isDone ? <Check weight="bold" className="w-5 h-5 text-white" /> :
                                 isActive && !isFailed ? <Spinner className="w-5 h-5 animate-spin" style={{ color: 'var(--accent)' }} /> :
                                 isFailed && isActive ? <WarningCircle weight="fill" className="w-5 h-5 text-red-400" /> :
                                 <span className="text-xs font-bold" style={{ color: 'var(--text-dimmed)' }}>{i + 1}</span>}
                              </div>
                              {i < STATUS_STEPS.length - 1 && <div className={`w-0.5 h-6 mt-1 ${isDone ? 'bg-emerald-500' : ''}`} style={!isDone ? { background: 'var(--border-main)' } : {}} />}
                            </div>
                            <div className="pt-2">
                              <p className={`font-bold text-sm ${isDone ? 'text-emerald-400' : ''}`} style={!isDone ? { color: isActive ? 'var(--text-primary)' : 'var(--text-dimmed)' } : {}}>{s.label}</p>
                              {isActive && <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{s.description}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {paymentStatus !== 'finished' && paymentStatus !== 'failed' && paymentStatus !== 'expired' && (
                      <div className="mt-6 flex items-center justify-between rounded-lg p-3" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-main)' }}>
                        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <ArrowClockwise className="w-4 h-4" /> Auto-checking in <span className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{autoCheckCountdown}s</span>
                        </div>
                        <button onClick={() => { checkPaymentStatus(); setAutoCheckCountdown(15); }} className="text-sm font-medium" style={{ color: 'var(--accent)' }} data-testid="refresh-status-btn">Refresh Now</button>
                      </div>
                    )}
                  </div>

                  {/* Payment Details */}
                  {paymentStatus !== 'finished' && (
                    <div className="border rounded-xl p-6 space-y-5" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                      <div>
                        <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Transaction ID</p>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-bold truncate">{paymentData.payment_id}</span>
                          <button onClick={() => handleCopy(paymentData.payment_id, 'txid')} className="p-1.5 rounded transition-colors flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                            {copied === 'txid' ? <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Send exactly</p>
                        <div className="flex items-center gap-3">
                          <p className="text-2xl font-black">{paymentData.pay_amount}</p>
                          <p className="text-lg font-bold" style={{ color: 'var(--text-secondary)' }}>{paymentData.pay_currency}</p>
                          <button onClick={() => handleCopy(String(paymentData.pay_amount), 'amount')} className="p-1.5 rounded transition-colors" style={{ color: 'var(--text-muted)' }}>
                            {copied === 'amount' ? <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>To this address</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 border p-3 rounded-lg font-mono text-xs break-all" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)' }} data-testid="pay-address">{paymentData.pay_address}</div>
                          <button onClick={() => handleCopy(paymentData.pay_address, 'address')} className="p-2.5 rounded-lg transition-colors flex-shrink-0" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }} data-testid="copy-address-btn">
                            {copied === 'address' ? <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-center">
                        <div className="bg-white p-4 rounded-xl inline-block" data-testid="payment-qr-code">
                          <QRCodeSVG value={paymentData.pay_address || ''} size={180} level="H" bgColor="#ffffff" fgColor="#0a0a0a" />
                        </div>
                      </div>
                      <p className="text-center text-xs" style={{ color: 'var(--text-muted)' }}>Scan QR code to send payment</p>
                    </div>
                  )}

                  {paymentStatus === 'finished' && (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6 text-center">
                      <CheckCircle weight="fill" className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
                      <h3 className="text-xl font-bold mb-2">Payment Complete!</h3>
                      <p className="text-emerald-400 mb-6">{paymentData.credits?.toLocaleString()} credits have been added.</p>
                      <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2" data-testid="go-to-dashboard">Go to Dashboard <CaretRight weight="bold" className="w-4 h-4" /></Link>
                    </div>
                  )}

                  {(paymentStatus === 'failed' || paymentStatus === 'expired') && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
                      <WarningCircle weight="fill" className="w-16 h-16 text-red-400 mx-auto mb-3" />
                      <h3 className="text-xl font-bold mb-2">Payment {paymentStatus === 'failed' ? 'Failed' : 'Expired'}</h3>
                      <p className="text-red-400 mb-6">Please try again.</p>
                      <Link to="/pricing" className="btn-primary inline-flex items-center gap-2">Try Again</Link>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ========= INR / UPI PAYMENT FLOW ========= */}
          {paymentMethod === 'inr' && (
            <div className="space-y-6">
              {inrStep === 'pay' && (
                <>
                  {/* UPI Payment Info */}
                  {upiInfo ? (
                    <div className="border rounded-xl p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                      <h2 className="text-xl font-bold mb-5 flex items-center gap-2">
                        <Bank weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                        Pay via UPI
                      </h2>

                      {/* Amount to pay */}
                      <div className="rounded-xl p-5 mb-5 text-center" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }}>
                        <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Amount to Pay</p>
                        <p className="text-4xl font-black" style={{ color: 'var(--accent)' }}>₹{planInr.toLocaleString()}</p>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{plan.name} Plan - {plan.credits.toLocaleString()} Credits</p>
                      </div>

                      {/* UPI ID */}
                      <div className="mb-5">
                        <p className="text-xs mb-2 font-medium" style={{ color: 'var(--text-muted)' }}>Send payment to this UPI ID</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 border p-3 rounded-lg font-mono text-sm font-bold" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)' }} data-testid="upi-id-display">
                            {upiInfo.upi_id}
                          </div>
                          <button
                            onClick={() => handleCopy(upiInfo.upi_id, 'upi')}
                            className="p-2.5 rounded-lg transition-colors flex-shrink-0"
                            style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}
                            data-testid="copy-upi-btn"
                          >
                            {copied === 'upi' ? <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>
                        {upiInfo.display_name && (
                          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Name: {upiInfo.display_name}</p>
                        )}
                      </div>

                      {/* UPI QR Code */}
                      <div className="flex justify-center mb-5">
                        <div className="bg-white p-4 rounded-xl inline-block">
                          <QRCodeSVG value={`upi://pay?pa=${upiInfo.upi_id}&pn=${encodeURIComponent(upiInfo.display_name || 'index24hrs.com')}&am=${planInr}&cu=INR`} size={180} level="H" bgColor="#ffffff" fgColor="#0a0a0a" />
                        </div>
                      </div>
                      <p className="text-center text-xs mb-5" style={{ color: 'var(--text-muted)' }}>Scan with any UPI app (GPay, PhonePe, Paytm)</p>

                      {/* UTR Input */}
                      <div className="border-t pt-5" style={{ borderColor: 'var(--border-main)' }}>
                        <label className="block text-sm font-bold mb-2">Enter UTR / Transaction Reference Number</label>
                        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>After making the payment, enter the UTR number from your UPI app</p>
                        <input
                          type="text"
                          value={utrNumber}
                          onChange={(e) => setUtrNumber(e.target.value)}
                          placeholder="e.g. 412345678901"
                          className="w-full border rounded-lg px-4 py-3 text-sm font-mono mb-4"
                          style={{ background: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
                          data-testid="utr-input"
                        />
                        <button
                          onClick={handleSubmitUtr}
                          disabled={inrLoading || !utrNumber.trim()}
                          className="w-full btn-primary py-3.5 flex items-center justify-center gap-2 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="submit-utr-btn"
                        >
                          {inrLoading ? <Spinner className="w-5 h-5 animate-spin" /> : (
                            <><CheckSquare weight="fill" className="w-5 h-5" /> Submit Payment</>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border rounded-xl p-8 text-center" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
                      <Bank weight="duotone" className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                      <p className="font-bold mb-1">INR/UPI Payment Not Available</p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>UPI payment has not been configured yet. Please use crypto or contact support.</p>
                    </div>
                  )}

                  {/* Important Note */}
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
                    <div className="flex gap-3">
                      <WarningCircle weight="fill" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-sm text-amber-400 mb-2">Important</h3>
                        <ul className="text-xs space-y-1.5" style={{ color: 'var(--text-secondary)' }}>
                          <li>Send the <strong className="text-amber-300">exact amount</strong> shown above via UPI.</li>
                          <li>After payment, enter your <strong className="text-amber-300">UTR number</strong> to submit for verification.</li>
                          <li>Credits will be added after <strong className="text-amber-300">admin approval</strong> (usually within minutes).</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {inrStep === 'submitted' && (
                <div className="border rounded-xl p-8 text-center" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.15)' }}>
                    <ClockCounterClockwise weight="duotone" className="w-8 h-8" style={{ color: 'var(--accent)' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Payment Submitted!</h3>
                  <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                    Your payment is pending admin approval. Credits will be added once verified.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button onClick={() => setInrStep('pay')} className="px-5 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: 'var(--border-main)', color: 'var(--text-secondary)' }}>
                      Submit Another
                    </button>
                    <Link to="/dashboard" className="btn-primary px-5 py-2.5 inline-flex items-center gap-2 text-sm">
                      Go to Dashboard <CaretRight weight="bold" className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}

              {/* INR Payment History */}
              {inrHistory.length > 0 && (
                <div className="border rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                  <div className="p-4 border-b" style={{ borderColor: 'var(--border-main)' }}>
                    <h3 className="font-bold flex items-center gap-2">
                      <ClockCounterClockwise className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                      INR Payment History
                    </h3>
                  </div>
                  <div className="divide-y" style={{ borderColor: 'var(--border-main)' }}>
                    {inrHistory.map((p, i) => (
                      <div key={i} className="p-4 flex items-center justify-between" style={{ borderColor: 'var(--border-main)' }}>
                        <div>
                          <p className="font-bold text-sm">{p.plan_name} - ₹{(p.amount_inr || 0).toLocaleString()}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>UTR: {p.utr_number} | {new Date(p.created_at).toLocaleDateString()}</p>
                        </div>
                        {getInrStatusBadge(p.status)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
